import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Users, ShieldAlert, UsersRound, AlertCircle, Search, X, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { useAuth } from '@/context/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { SiteHeader } from '@/components/site-header';
import { EmptyState } from '@/components/ui/empty-state';
import MultiAgentCard, { MultiAgentSkeletonGrid } from './components/MultiAgentCard';
import { getMultiAgents } from '@/api/multi-agent/multi_agent.api';
import { MultiAgent } from '@/api/multi-agent/multi_agent.types';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    TooltipProvider,
} from "@/components/ui/tooltip";
import { MobileOverlay } from '@/components/ui/mobile-overlay';
import { containerVariants, itemVariants } from '@/components/animations';

const MultiAgents = () => {
    const navigate = useNavigate();
    const { error } = useEnhancedToast();
    const { user } = useAuth();
    const isMobile = useIsMobile();
    const [isLoading, setIsLoading] = useState(true);
    const [multiAgents, setMultiAgents] = useState<MultiAgent[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showMobileOverlay, setShowMobileOverlay] = useState(false);

    const canCreateAgents = user?.permissions?.multi_agent === "EDITOR";

    // Filter multi agents based on search query
    const filteredMultiAgents = useMemo(() => {
        if (!searchQuery.trim()) return multiAgents;
        
        const query = searchQuery.toLowerCase().trim();
        return multiAgents.filter(agent =>
            agent.name.toLowerCase().includes(query) ||
            agent.id.toLowerCase().includes(query)
        );
    }, [multiAgents, searchQuery]);

    useEffect(() => {
        loadMultiAgents();
    }, []);

    const loadMultiAgents = async () => {
        try {
            setIsLoading(true);
            const response = await getMultiAgents();
            if (response.error) {
                throw new Error(response.error);
            }
            setMultiAgents(response.data || []);
        } catch (err: unknown) {
            // console.error('Error loading Multi Agents:', err);
            error({
                icon: AlertCircle,
                title: "Failed to Load Multi Agents",
                description: err.message || "Could not load multi agents. Please try again.",
                duration: 4000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleNewMultiAgents = () => {
        if (isMobile) {
            setShowMobileOverlay(true);
        } else {
            navigate('/dashboard/multi-agent/new', { state: { isNewMultiAgents: true } });
        }
    };

    // const handleTemplateSelect = (template: any) => {
    //     navigate(`/dashboard/multi-agent/new?template=${template.id}`, { state: { isNewMultiAgents: true } });
    // };

    const handleAgentClick = (agent: MultiAgent) => {
        if (isMobile) {
            setShowMobileOverlay(true);
        } else {
            navigate(`/dashboard/multi-agent/${agent.id}`);
        }
    };

    return (
        <div className="flex flex-col h-full">
            <SiteHeader
                title="Multi Agents"
                subtitle="Manage your multi-agent systems and workflows"
            >
                {multiAgents.length > 0 && (
                    <>
                        {/* Search Input */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                            <Input
                                placeholder="Search by name or ID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-9 w-[200px] lg:w-[300px] pl-9 pr-9"
                            />
                            {searchQuery && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </Button>
                            )}
                        </div>

                        {/* Create Button */}
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div>
                                        <Button
                                            onClick={handleNewMultiAgents}
                                            size="sm"
                                            className="gap-2"
                                            disabled={!canCreateAgents}
                                        >
                                            <Plus className="h-4 w-4" />
                                            New Multi Agent
                                            {!canCreateAgents && <Lock className="h-3.5 w-3.5 opacity-70" />}
                                        </Button>
                                    </div>
                                </TooltipTrigger>
                                {!canCreateAgents && (
                                    <TooltipContent className="flex items-center gap-2 max-w-xs">
                                        <ShieldAlert className="w-12 h-12 text-amber-500" />
                                        <p>You need editor permissions to create multi agents. Contact your administrator for access.</p>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </TooltipProvider>
                    </>
                )}
            </SiteHeader>

            <div className="flex-1 overflow-auto">
                {isLoading ? (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        <MultiAgentSkeletonGrid />
                    </motion.div>
                ) : multiAgents.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            className="w-full -mt-12"
                        >
                            <EmptyState
                                icon={Users}
                                title="No Multi Agents Yet"
                                description="Create your first multi-agent system to orchestrate complex AI workflows."
                                action={{
                                    label: "New Multi Agent",
                                    onClick: handleNewMultiAgents,
                                    icon: Plus,
                                    disabled: !canCreateAgents,
                                    disabledReason: "You need editor permissions to create multi agents. Contact your administrator for access.",
                                }}
                            />
                        </motion.div>
                    </div>
                ) : filteredMultiAgents.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className="h-full flex items-center justify-center"
                    >
                        <div className="text-center">
                            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                                <Search className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2">No Matching Multi Agents</h3>
                            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                                We couldn't find any multi agents matching your search query.
                            </p>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSearchQuery('')}
                            >
                                Clear Search
                            </Button>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        <motion.div 
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                        >
                            {filteredMultiAgents.map((agent) => (
                                <MultiAgentCard
                                    key={agent.id}
                                    agent={agent}
                                    onClick={handleAgentClick}
                                    variants={itemVariants}
                                />
                            ))}
                        </motion.div>
                    </motion.div>
                )}
            </div>

            {/* Mobile Overlay */}
            {showMobileOverlay && (
                <MobileOverlay
                    icon={UsersRound}
                    title="Multi Agent Configuration"
                    description="Multi Agent configuration is currently optimized for desktop view. Please use a desktop browser for the best experience."
                    onBack={() => setShowMobileOverlay(false)}
                />
            )}
        </div>
    );
};

export default MultiAgents;
